import React, { useMemo, useState } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useAuth } from '@/core/hooks';
import { getPrimaryRole, getRoleLabel } from '@/core/utils/role-routing';
import { formatCompanyAddress, useCompanyProfile } from '@/app/utils/companyProfile';

export interface AppShellProps {
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useAuth();
  const companyProfile = useCompanyProfile();
  const companyAddress = formatCompanyAddress(companyProfile);
  const roleLabel = useMemo(() => {
    const role = user ? getPrimaryRole(user.roles) : null;
    return role ? getRoleLabel(role) : 'User';
  }, [user]);

  return (
    <div className="app-shell min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-950 dark:text-slate-100">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="lg:pl-64">
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <section className="brand-ribbon border-b border-purple-100/80 bg-white/76 px-4 py-3 shadow-sm shadow-slate-950/5 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/72 md:px-6 lg:px-8">
          <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="hidden h-14 w-14 shrink-0 place-items-center rounded-md bg-[#f7effa] ring-1 ring-[#dec6e8] sm:grid">
                <img src={companyProfile.logoMarkUrl} alt="" className="h-12 w-12 object-contain" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#6c3a80] dark:text-fuchsia-200">
                  {companyProfile.publicName}
                </p>
                <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Signed in as {user?.name || 'team member'} · {roleLabel}
                </p>
              </div>
            </div>
            <div className="grid gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 sm:grid-cols-3 sm:text-right">
              <span>{companyAddress}</span>
              <span>Tel: {companyProfile.phone}</span>
              <span>On-call: {companyProfile.onCallPhone}</span>
            </div>
          </div>
        </section>

        <main id="app-document-content" className="p-4 pb-24 md:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-[1600px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
